import useInterval from "@via/hooks/useInterval";
import { createContext, useCallback, useState } from "react";
import {
  isHandlingUpgradeRequiredError,
  resetUpgradeRequiredFlag,
} from "@via/api/axios.tsx";

const INTERVAL = 1000;
const DEFAULT_TIMEOUT = 5000;

// Assign a NOTIFICATION type based on number for common types of alerts
export enum NOTIFICATION {
  ALERT,
  ERROR,
  SUCCESS,
}

export interface INotification {
  id: string | number | 1;
  message: string;
  details: string;
  variant: NOTIFICATION;
  timestamp: number;
  timeout?: number | null; // pass in null for no timeout
}

const defaultApi = {
  notifications: [] as INotification[],
  setNotification: (_notification?: any) => null,
  clearNotification: (_id?: string | number | number[]) => null,
};

export type NotificationsContextApi = typeof defaultApi;

/**
 * Create Context
 */
export const NotificationsContext =
  createContext<NotificationsContextApi>(defaultApi);

/**
 * Custom Notifications Provider
 */
export function NotificationsProvider({ children }: any): any {
  const [notifications, setNotifications] = useState<INotification[]>(
    defaultApi.notifications
  );

  // Append a new notification (or override existing by id)
  const setNotification = useCallback(
    (notification: any) => {
      // Don't show notifications if we're handling an upgrade required error
      if (isHandlingUpgradeRequiredError()) {
        resetUpgradeRequiredFlag(); // Reset the flag after checking
        return;
      }

      setNotifications(currentNotifications => {
        const existing = currentNotifications.find(
          n => n.id === notification.id
        );
        const nextNotifications =
          existing != null
            ? currentNotifications.map(n =>
                n.id === notification.id ? { ...existing, ...notification } : n
              )
            : currentNotifications.concat({
                id: `${Math.random()}_${Date.now()}`,
                timestamp: new Date().getTime(),
                variant: NOTIFICATION.ERROR,
                ...notification,
              });
        return nextNotifications;
      });
    },
    [setNotifications]
  );

  // Clear notification(s) by id, or clear ALL notifications
  const clearNotification = useCallback(
    (id: string | number | number[] = []) => {
      if (id != null) {
        setNotifications([]);
      } else {
        const ids: any = Array.isArray(id) ? id : [id];
        const nextNotifications = notifications.filter(({ id }) =>
          ids?.includes(id)
        );
        setNotifications(nextNotifications);
      }
    },
    [notifications, setNotifications]
  );

  // Set up interval to auto-expire notifications
  const handleExpireNotifications = useCallback(
    (currentTime: number): any => {
      if (notifications.length > 0) {
        const expiredIds = notifications.reduce((acc: any, n: any) => {
          const isExpired =
            n.timestamp <= currentTime - (n.timeout ?? DEFAULT_TIMEOUT);
          return isExpired && n.timeout !== null ? acc.concat(n.id) : acc;
        }, []);
        if (expiredIds.length > 0) {
          clearNotification(expiredIds);
        }
      }
    },
    [notifications, clearNotification]
  );

  useInterval(handleExpireNotifications, INTERVAL);

  // Return Provider with Notifications API
  return (
    <NotificationsContext.Provider
      value={
        {
          notifications,
          setNotification,
          clearNotification,
          NOTIFICATION,
        } as any
      }>
      {children}
    </NotificationsContext.Provider>
  );
}
